import { useCallback, useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ColumnConfig } from './useColumnVisibility';
import { listingSettingsService, type ListingKey } from '../services/listingSettings';

type SortOrder = 'ascend' | 'descend' | null;

export const useListingSortPersistence = <T extends ColumnConfig>(
  listingKey: ListingKey,
  baseColumns: T[],
  setSortKey: Dispatch<SetStateAction<string | null>>,
  setSortOrder: Dispatch<SetStateAction<SortOrder>>
) => {
  const allowedKeys = useMemo(() => new Set(baseColumns.map((col) => String(col.key))), [baseColumns]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const settings = await listingSettingsService.getListingSettings(listingKey);
        if (cancelled || !settings) {
          return;
        }
        const nextKey = settings.sort_key;
        const nextOrder = settings.sort_order;
        if (!nextKey || !nextOrder) {
          return;
        }
        if (!allowedKeys.has(nextKey)) {
          return;
        }
        setSortKey(nextKey);
        setSortOrder(nextOrder);
      } catch {
        // Ignore loading errors and fall back to defaults
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [allowedKeys, listingKey, setSortKey, setSortOrder]);

  const persistSort = useCallback(
    async (nextKey: string | null, nextOrder: SortOrder) => {
      try {
        await listingSettingsService.updateListingSettings(listingKey, {
          sort_key: nextKey,
          sort_order: nextOrder,
        });
      } catch {
        // Ignore persistence errors
      }
    },
    [listingKey]
  );

  return { persistSort };
};
